import { Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { DatePipe, NgForOf, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Swing } from '../../../models/swing';
import { SessionInfo } from '../../../models/sessioninfo';
import { SessionService } from '../../../services/session.service';
import { ConfigService } from '../../../services/configuration.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { makeWebmSeekable } from '../../../utils/webm-seekable';
import { swingResultLabel } from '../../../utils/swing-result';
import { PERMISSIONS } from '../../../models/constants';
import { Player } from '../../../models/player';

type Camera = 'faceon' | 'dtl';
type Side = 'left' | 'right';
type PickerSide = Side | null;
type Shape =
  | { type: 'circle'; cx: number; cy: number; r: number; selected: boolean }
  | { type: 'line'; x1: number; y1: number; x2: number; y2: number; selected: boolean };

interface SessionSwingGroup {
  session: SessionInfo;
  swings: Swing[];
  swingsLoaded: boolean;
  swingsLoading: boolean;
  expanded: boolean;
}

interface ComparePane {
  swing: Swing | null;
  sessionLabel: string;
  videoUrl: string;
  hasClip: boolean;
  isLoading: boolean;
  currentTime: number;
  duration: number;
  videosFullyLocal: boolean;
  loadToken: number;
  objectUrls: string[];
  seekPump: ReturnType<typeof setInterval> | null;
  desiredTime: number;
  lastCommitAt: number;
  seekIssuedAt: number;
  isScrubbing: boolean;
  startAppliedToken: number;
}

@Component({
  selector: 'app-compare-swings',
  standalone: true,
  imports: [NgIf, NgForOf, FormsModule, DatePipe],
  templateUrl: './compare-swings.component.html',
  styleUrl: './compare-swings.component.css'
})
export class CompareSwingsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() username = '';
  @Input() currentSessionId: number | null = null;
  @Input() currentSessionLabel = '';
  @Input() anchorSwing: Swing | null = null;

  @ViewChild('leftVideo') leftVideoElement?: ElementRef<HTMLVideoElement>;
  @ViewChild('rightVideo') rightVideoElement?: ElementRef<HTMLVideoElement>;

  activeCamera: Camera = 'faceon';
  playbackSpeed = 0.5;
  isPlaying = false;
  pickerSide: PickerSide = null;
  pickerFilter = '';
  catalog: SessionSwingGroup[] = [];
  catalogLoaded = false;
  catalogLoading = false;
  pickerUsername = '';
  canCompareToOtherSwings = false;
  players: Player[] = [];
  private pickerPermissionsReady = false;
  private catalogsByUser = new Map<string, SessionSwingGroup[]>();
  leftUserPicked = false;
  savingStartSide: Side | null = null;
  private isLinkedScrubbing = false;
  private readonly onPointerUpScrubEnd = () => this.finishScrub(true);

  linkedTime = 0;
  linkedDuration = 0;
  private originLeft = 0;
  private originRight = 0;
  readonly sliderStep = 1 / 60;
  readonly frameStep = 1 / 30;
  private readonly minCommitIntervalMs = 150;
  private readonly seekTimeoutMs = 1500;

  left: ComparePane = this.emptyPane();
  right: ComparePane = this.emptyPane();

  leftShapes: Shape[] = [];
  rightShapes: Shape[] = [];
  isDrawing = false;
  isDragging = false;
  currentShape: Shape | null = null;
  mode: 'circle' | 'line' | null = null;
  selectedShape: Shape | null = null;
  private dragStartPoint: { x: number; y: number } | null = null;
  private dragOrigin: { cx?: number; cy?: number; x1?: number; y1?: number; x2?: number; y2?: number } | null = null;
  private shapeMoved = false;
  private readonly dragThreshold = 4;

  constructor(
    private sessionService: SessionService,
    private configService: ConfigService,
    private authenticationService: AuthenticationService,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.pickerUsername = this.username;
    this.initPickerPermissions();
    if (this.anchorSwing && !this.left.swing) {
      this.applySwing('left', this.anchorSwing, this.currentSessionLabel || 'This session', false);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['anchorSwing'] && this.anchorSwing && !this.leftUserPicked) {
      this.applySwing('left', this.anchorSwing, this.currentSessionLabel || 'This session', false);
    }
    if (changes['username'] && this.username) {
      this.pickerUsername = this.username;
      this.catalogLoaded = false;
      this.catalog = [];
      this.catalogsByUser.clear();
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('pointerup', this.onPointerUpScrubEnd);
    this.pauseBoth();
    this.stopPanePump(this.left);
    this.stopPanePump(this.right);
    this.releasePaneUrls(this.left);
    this.releasePaneUrls(this.right);
    this.setDrawingScrollLock(false);
  }

  resultLabel(swing: Swing | null): string {
    return swing ? swingResultLabel(swing.result) : '';
  }

  paneTitle(pane: ComparePane): string {
    if (!pane.swing) {
      return 'Select a swing';
    }
    const thumbs = this.resultLabel(pane.swing);
    return `${pane.sessionLabel} · Swing #${pane.swing.sequence}${thumbs ? ' ' + thumbs : ''}`;
  }

  savedStartTime(pane: ComparePane): number | null {
    if (!pane.swing) {
      return null;
    }
    const value = this.activeCamera === 'dtl' ? pane.swing.dtlStartTime : pane.swing.faceOnStartTime;
    return value == null || !isFinite(value) ? null : value;
  }

  async markStart(side: Side) {
    const pane = this.pane(side);
    if (!pane.swing || !pane.hasClip || this.savingStartSide) {
      return;
    }

    this.pauseBoth();
    const startTime = +pane.currentTime.toFixed(4);
    this.savingStartSide = side;
    const success = await this.sessionService.saveSwingStartTime(pane.swing.id, this.activeCamera, startTime);
    this.savingStartSide = null;
    if (!success) {
      return;
    }

    this.writeStartTime(pane.swing, startTime);
    this.syncStartTimeInCatalog(pane.swing.id, startTime);
    if (side === 'left') {
      this.originLeft = startTime;
    } else {
      this.originRight = startTime;
    }
    this.linkedTime = 0;
    this.updateLinkedDuration();
  }

  setCamera(camera: Camera | string) {
    this.activeCamera = camera === 'dtl' ? 'dtl' : 'faceon';
    this.reloadPaneCamera(this.left);
    this.reloadPaneCamera(this.right);
  }

  async openPicker(side: Side) {
    this.pickerSide = side;
    this.pickerFilter = '';
    if (!this.pickerUsername) {
      this.pickerUsername = this.username;
    }
    await this.initPickerPermissions();
    await this.loadCatalog();
  }

  async onPickerPlayerChange() {
    this.pickerFilter = '';
    this.catalogLoaded = false;
    await this.loadCatalog();
  }

  closePicker() {
    this.pickerSide = null;
  }

  filteredCatalog(): SessionSwingGroup[] {
    const q = this.pickerFilter.trim().toLowerCase();
    if (!q) {
      return this.catalog;
    }
    return this.catalog.filter(group => {
      if (this.sessionMatchesFilter(group.session, q)) {
        return true;
      }
      return group.swings.some(swing => this.swingMatchesFilter(group.session, swing, q));
    });
  }

  swingsForGroup(group: SessionSwingGroup): Swing[] {
    const q = this.pickerFilter.trim().toLowerCase();
    if (!q || this.sessionMatchesFilter(group.session, q)) {
      return group.swings;
    }
    return group.swings.filter(swing => this.swingMatchesFilter(group.session, swing, q));
  }

  async toggleSession(group: SessionSwingGroup) {
    group.expanded = !group.expanded;
    if (group.expanded) {
      await this.ensureSwingsLoaded(group);
    }
  }

  pickSwing(group: SessionSwingGroup, swing: Swing) {
    if (this.pickerSide === 'left') {
      this.leftUserPicked = true;
      this.applySwing('left', swing, this.swingSourceLabel(group), true);
    } else if (this.pickerSide === 'right') {
      this.applySwing('right', swing, this.swingSourceLabel(group), true);
    }
    this.closePicker();
  }

  onPaneScrubStart(side: Side) {
    this.pauseForScrub();
    this.left.isScrubbing = side === 'left';
    this.right.isScrubbing = side === 'right';
    this.isLinkedScrubbing = false;
    this.listenForScrubEnd();
  }

  onPaneScrub(side: Side, value: number | string) {
    const pane = this.pane(side);
    pane.isScrubbing = true;
    this.seekPane(pane, Number(value));
  }

  onPaneScrubEnd(_side: Side) {
    this.finishScrub(true);
  }

  onLinkedScrubStart() {
    this.pauseForScrub();
    this.left.isScrubbing = true;
    this.right.isScrubbing = true;
    this.isLinkedScrubbing = true;
    this.listenForScrubEnd();
  }

  onLinkedScrub(value: number | string) {
    this.isLinkedScrubbing = true;
    this.left.isScrubbing = true;
    this.right.isScrubbing = true;
    this.linkedTime = Number(value);
    this.seekPane(this.left, this.originLeft + this.linkedTime);
    this.seekPane(this.right, this.originRight + this.linkedTime);
  }

  playBoth() {
    this.finishScrub(false);
    this.stopPanePump(this.left);
    this.stopPanePump(this.right);
    this.getVideos().forEach(video => {
      video.playbackRate = Number(this.playbackSpeed) || 1;
      video.play()?.catch(() => {});
    });
    this.isPlaying = true;
  }

  pauseBoth() {
    this.getVideos().forEach(video => video.pause());
    this.isPlaying = false;
    if (!this.left.isScrubbing && !this.right.isScrubbing && !this.isLinkedScrubbing) {
      this.left.currentTime = this.videoTime(this.leftVideoElement);
      this.right.currentTime = this.videoTime(this.rightVideoElement);
      this.syncLinkedFromPanes();
    }
  }

  resetBoth() {
    this.finishScrub(false);
    this.pauseBoth();
    this.linkedTime = 0;
    this.seekPane(this.left, this.originLeft);
    this.seekPane(this.right, this.originRight);
  }

  onVideoMetadata(side: Side) {
    const pane = this.pane(side);
    const video = this.videoEl(side);
    if (video) {
      pane.duration = this.videoDuration(video);
    }
    this.updateLinkedDuration();
  }

  onVideoCanPlay(side: Side) {
    this.pane(side).isLoading = false;
    this.onVideoMetadata(side);
    this.applySavedStartIfNeeded(side);
  }

  onVideoLoadError(side: Side) {
    this.pane(side).isLoading = false;
  }

  onTimeUpdate(side: Side, event: Event) {
    const pane = this.pane(side);
    if (pane.isScrubbing || this.isLinkedScrubbing || this.isDrawing || this.isDragging) {
      return;
    }
    const video = event.target as HTMLVideoElement;
    if (!isFinite(video.currentTime) || video.seeking) {
      return;
    }
    pane.currentTime = video.currentTime;
    pane.desiredTime = pane.currentTime;
    if (this.isPlaying) {
      this.syncLinkedFromPanes();
    }
  }

  onPlaybackEnded() {
    this.pauseBoth();
  }

  scrubPercent(pane: ComparePane): string {
    if (!pane.duration) {
      return '0%';
    }
    return `${Math.min(100, Math.max(0, (pane.currentTime / pane.duration) * 100))}%`;
  }

  linkedPercent(): string {
    if (!this.linkedDuration) {
      return '0%';
    }
    return `${Math.min(100, Math.max(0, (this.linkedTime / this.linkedDuration) * 100))}%`;
  }

  formatTime(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) {
      return '0.00s';
    }
    return `${seconds.toFixed(2)}s`;
  }

  paneDisabled(pane: ComparePane): boolean {
    return pane.duration <= 0 || pane.isLoading || !pane.hasClip;
  }

  linkedDisabled(): boolean {
    return this.paneDisabled(this.left) || this.paneDisabled(this.right);
  }

  private emptyPane(): ComparePane {
    return {
      swing: null,
      sessionLabel: '',
      videoUrl: '',
      hasClip: false,
      isLoading: false,
      currentTime: 0,
      duration: 0,
      videosFullyLocal: false,
      loadToken: 0,
      objectUrls: [],
      seekPump: null,
      desiredTime: 0,
      lastCommitAt: 0,
      seekIssuedAt: 0,
      isScrubbing: false,
      startAppliedToken: -1
    };
  }

  private pane(side: Side): ComparePane {
    return side === 'left' ? this.left : this.right;
  }

  private writeStartTime(swing: Swing, startTime: number | null) {
    if (this.activeCamera === 'dtl') {
      swing.dtlStartTime = startTime;
    } else {
      swing.faceOnStartTime = startTime;
    }
  }

  private syncStartTimeInCatalog(swingId: string, startTime: number | null) {
    this.catalog.forEach(group => {
      group.swings.forEach(swing => {
        if (swing.id === swingId) {
          this.writeStartTime(swing, startTime);
        }
      });
    });
    if (this.anchorSwing?.id === swingId) {
      this.writeStartTime(this.anchorSwing, startTime);
    }
  }

  private applySavedStartIfNeeded(side: Side) {
    const pane = this.pane(side);
    if (pane.startAppliedToken === pane.loadToken || !pane.hasClip) {
      return;
    }
    pane.startAppliedToken = pane.loadToken;
    const start = this.savedStartTime(pane) ?? 0;
    this.seekPane(pane, start);
    if (side === 'left') {
      this.originLeft = start;
    } else {
      this.originRight = start;
    }
    this.linkedTime = 0;
    this.updateLinkedDuration();
  }

  private applySwing(side: Side, swing: Swing, sessionLabel: string, resetOrigins: boolean) {
    const pane = this.pane(side);
    pane.swing = swing;
    pane.sessionLabel = sessionLabel;
    pane.currentTime = 0;
    pane.duration = 0;
    pane.desiredTime = 0;
    this.reloadPaneCamera(pane);
    if (resetOrigins) {
      this.originLeft = this.left.currentTime;
      this.originRight = this.right.currentTime;
      this.linkedTime = 0;
      this.updateLinkedDuration();
    }
  }

  private reloadPaneCamera(pane: ComparePane) {
    const swing = pane.swing;
    if (!swing) {
      pane.hasClip = false;
      pane.videoUrl = '';
      pane.isLoading = false;
      return;
    }
    const path = ((this.activeCamera === 'faceon' ? swing.faceOnUrl : swing.dtlUrl) ?? '').trim();
    pane.hasClip = path.length > 0;
    this.loadPaneVideo(pane, pane.hasClip ? this.configService.getMediaBaseUrl() + path : '');
  }

  private loadPaneVideo(pane: ComparePane, url: string) {
    const token = ++pane.loadToken;
    this.releasePaneUrls(pane);
    pane.videoUrl = '';
    pane.videosFullyLocal = false;
    pane.duration = 0;
    pane.isLoading = pane.hasClip;
    pane.startAppliedToken = -1;

    if (!url) {
      pane.isLoading = false;
      return;
    }

    if (!this.isLegacyWebmUrl(url)) {
      pane.videoUrl = url;
      pane.videosFullyLocal = true;
      pane.isLoading = true;
      return;
    }

    this.downloadToObjectUrl(url).then(local => {
      if (token !== pane.loadToken) {
        if (local) {
          URL.revokeObjectURL(local);
        }
        return;
      }
      if (local) {
        pane.objectUrls.push(local);
        pane.videoUrl = local;
        pane.videosFullyLocal = true;
      } else {
        pane.videoUrl = url;
        pane.videosFullyLocal = false;
      }
    });
  }

  private isLegacyWebmUrl(url: string): boolean {
    return /\.webm(\?|#|$)/i.test(url);
  }

  private async downloadToObjectUrl(url: string): Promise<string | null> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        return null;
      }
      const raw = await response.blob();
      const seekable = await makeWebmSeekable(raw);
      return URL.createObjectURL(seekable);
    } catch {
      return null;
    }
  }

  private releasePaneUrls(pane: ComparePane) {
    pane.objectUrls.forEach(url => URL.revokeObjectURL(url));
    pane.objectUrls = [];
  }

  private seekPane(pane: ComparePane, time: number) {
    const max = pane.duration > 0 ? pane.duration : Math.max(0, time);
    const safeTime = Math.max(0, Math.min(max, time));
    pane.currentTime = +safeTime.toFixed(4);
    pane.desiredTime = pane.currentTime;
    this.startPanePump(pane);
  }

  private startPanePump(pane: ComparePane) {
    if (pane.seekPump) {
      return;
    }
    this.pumpPane(pane);
    pane.seekPump = setInterval(() => this.pumpPane(pane), 80);
  }

  private stopPanePump(pane: ComparePane) {
    if (pane.seekPump) {
      clearInterval(pane.seekPump);
      pane.seekPump = null;
    }
  }

  private pumpPane(pane: ComparePane) {
    const video = pane === this.left
      ? this.leftVideoElement?.nativeElement
      : this.rightVideoElement?.nativeElement;
    if (!video || this.videoDuration(video) <= 0) {
      return;
    }

    const now = performance.now();
    if (video.seeking) {
      if (now - pane.seekIssuedAt > this.seekTimeoutMs) {
        pane.desiredTime = video.currentTime;
        pane.currentTime = pane.desiredTime;
        this.stopPanePump(pane);
      }
      return;
    }

    const time = this.reachableTime(pane, video, pane.desiredTime);
    if (Math.abs(video.currentTime - time) < this.frameStep * 0.5) {
      if (!pane.isScrubbing) {
        this.stopPanePump(pane);
      }
      return;
    }

    if (pane.isScrubbing && now - pane.lastCommitAt < this.minCommitIntervalMs) {
      return;
    }

    pane.lastCommitAt = now;
    pane.seekIssuedAt = now;
    video.currentTime = time;
  }

  private reachableTime(pane: ComparePane, video: HTMLVideoElement, time: number): number {
    const wanted = Math.max(0, Math.min(this.videoDuration(video), time));
    if (pane.videosFullyLocal) {
      return wanted;
    }
    const ranges = video.seekable;
    if (!ranges || ranges.length === 0) {
      return wanted;
    }
    for (let i = 0; i < ranges.length; i++) {
      if (wanted >= ranges.start(i) && wanted <= ranges.end(i)) {
        return wanted;
      }
    }
    return Math.max(0, Math.min(wanted, ranges.end(ranges.length - 1)));
  }

  private videoDuration(video: HTMLVideoElement): number {
    if (isFinite(video.duration) && video.duration > 0) {
      return video.duration;
    }
    const ranges = video.seekable;
    return ranges && ranges.length > 0 ? ranges.end(ranges.length - 1) : 0;
  }

  private videoEl(side: Side): HTMLVideoElement | undefined {
    return side === 'left'
      ? this.leftVideoElement?.nativeElement
      : this.rightVideoElement?.nativeElement;
  }

  private videoTime(ref?: ElementRef<HTMLVideoElement>): number {
    const video = ref?.nativeElement;
    return video && isFinite(video.currentTime) ? video.currentTime : 0;
  }

  private getVideos(): HTMLVideoElement[] {
    const videos: HTMLVideoElement[] = [];
    if (this.leftVideoElement?.nativeElement?.src) {
      videos.push(this.leftVideoElement.nativeElement);
    }
    if (this.rightVideoElement?.nativeElement?.src) {
      videos.push(this.rightVideoElement.nativeElement);
    }
    return videos;
  }

  private captureOriginsFromCurrent() {
    this.originLeft = this.left.currentTime;
    this.originRight = this.right.currentTime;
    this.linkedTime = 0;
    this.updateLinkedDuration();
  }

  private pauseForScrub() {
    if (this.isPlaying) {
      this.getVideos().forEach(video => video.pause());
      this.isPlaying = false;
    }
  }

  private listenForScrubEnd() {
    window.removeEventListener('pointerup', this.onPointerUpScrubEnd);
    window.addEventListener('pointerup', this.onPointerUpScrubEnd);
  }

  finishScrub(captureOrigins: boolean) {
    const wasPaneScrub = (this.left.isScrubbing || this.right.isScrubbing) && !this.isLinkedScrubbing;
    this.left.isScrubbing = false;
    this.right.isScrubbing = false;
    this.isLinkedScrubbing = false;
    window.removeEventListener('pointerup', this.onPointerUpScrubEnd);
    if (captureOrigins && wasPaneScrub) {
      this.captureOriginsFromCurrent();
    }
  }

  private syncLinkedFromPanes() {
    const leftDelta = Math.max(0, this.left.currentTime - this.originLeft);
    const rightDelta = Math.max(0, this.right.currentTime - this.originRight);
    this.linkedTime = Math.min(leftDelta, rightDelta);
  }

  private updateLinkedDuration() {
    const leftRemain = Math.max(0, this.left.duration - this.originLeft);
    const rightRemain = Math.max(0, this.right.duration - this.originRight);
    if (this.left.duration > 0 && this.right.duration > 0) {
      this.linkedDuration = Math.min(leftRemain, rightRemain);
    } else {
      this.linkedDuration = 0;
    }
  }

  private async initPickerPermissions() {
    if (this.pickerPermissionsReady) {
      return;
    }
    this.canCompareToOtherSwings = await this.authenticationService.userHasPermission(PERMISSIONS.CANCOMPARETOOTHERSWINGS);
    if (this.canCompareToOtherSwings) {
      this.players = await this.authenticationService.getAllPlayers();
    }
    this.pickerPermissionsReady = true;
  }

  private pickerOwner(): string {
    return this.pickerUsername || this.username;
  }

  private swingSourceLabel(group: SessionSwingGroup): string {
    const playerName = this.playerDisplayName(group.session.username);
    if (playerName && this.canCompareToOtherSwings) {
      return `${playerName} · ${group.session.label}`;
    }
    return group.session.label;
  }

  playerDisplayName(username: string): string {
    const player = this.players.find(p => p.username === username);
    if (player) {
      return `${player.firstName} ${player.lastName}`.trim();
    }
    return username;
  }

  private async loadCatalog() {
    const owner = this.pickerOwner();
    if (!owner || this.catalogLoading) {
      return;
    }

    const cached = this.catalogsByUser.get(owner);
    if (cached) {
      this.catalog = cached;
      this.catalogLoaded = true;
      return;
    }

    this.catalogLoading = true;
    try {
      const sessions = await this.sessionService.searchSessions(owner);
      const isCurrentPlayer = owner === this.username;
      const groups: SessionSwingGroup[] = sessions.map(session => ({
        session,
        swings: [],
        swingsLoaded: false,
        swingsLoading: false,
        expanded: isCurrentPlayer && Number(session.id) === this.currentSessionId
      }));
      if (isCurrentPlayer) {
        groups.sort((a, b) => {
          const aCurrent = Number(a.session.id) === this.currentSessionId ? 0 : 1;
          const bCurrent = Number(b.session.id) === this.currentSessionId ? 0 : 1;
          return aCurrent - bCurrent;
        });
      }
      this.catalog = groups;
      this.catalogsByUser.set(owner, groups);
      this.catalogLoaded = true;
    } finally {
      this.catalogLoading = false;
    }

    const isCurrentPlayer = owner === this.username;
    if (isCurrentPlayer) {
      const current = this.catalog.find(group => Number(group.session.id) === this.currentSessionId);
      if (current) {
        current.expanded = true;
        await this.ensureSwingsLoaded(current);
      }
    }
  }

  private async ensureSwingsLoaded(group: SessionSwingGroup) {
    if (group.swingsLoaded || group.swingsLoading) {
      return;
    }
    group.swingsLoading = true;
    try {
      const swings = await this.sessionService.searchSwings(Number(group.session.id));
      group.swings = swings
        .filter(swing => this.swingHasVideo(swing))
        .slice()
        .sort((a, b) => b.sequence - a.sequence);
      group.swingsLoaded = true;
    } finally {
      group.swingsLoading = false;
    }
  }

  private swingHasVideo(swing: Swing): boolean {
    const faceOn = (swing.faceOnUrl ?? '').trim();
    const dtl = (swing.dtlUrl ?? '').trim();
    return faceOn.length > 0 || dtl.length > 0;
  }

  private sessionMatchesFilter(session: SessionInfo, q: string): boolean {
    return `${session.label} ${session.sessionDate}`.toLowerCase().includes(q);
  }

  private swingMatchesFilter(session: SessionInfo, swing: Swing, q: string): boolean {
    const thumbs = swingResultLabel(swing.result).toLowerCase();
    const haystack = [
      session.label,
      `swing #${swing.sequence}`,
      String(swing.sequence),
      swing.notes ?? '',
      ...(swing.tags ?? []),
      thumbs,
      swing.result ?? ''
    ].join(' ').toLowerCase();
    return haystack.includes(q);
  }

  toggleDrawMode(nextMode: 'line' | 'circle') {
    this.mode = this.mode === nextMode ? null : nextMode;
    this.setDrawingScrollLock(this.mode !== null);
  }

  exitDrawMode() {
    this.mode = null;
    this.setDrawingScrollLock(false);
  }

  @HostListener('document:keydown', ['$event'])
  handleDeleteKey(event: KeyboardEvent) {
    if (this.host.nativeElement.hidden) {
      return;
    }
    if (event.key === 'Delete' && this.selectedShape) {
      this.deleteSelectedShape();
      event.preventDefault();
    }
  }

  onMouseDown(event: MouseEvent, side: Side) {
    if (!this.mode) {
      return;
    }
    event.preventDefault();
    this.capturePointer(event);
    const arrayToManage = this.shapesFor(side);
    if (!event || !event.target) return;
    const pt = this.getSVGPoint(this.getOverlaySvg(event), event);
    if (!pt) return;

    this.clearSelection(side);
    this.shapeMoved = false;

    if (this.mode === 'circle') {
      this.currentShape = { type: 'circle', cx: pt.x, cy: pt.y, r: 0, selected: false };
    } else {
      this.currentShape = { type: 'line', x1: pt.x, y1: pt.y, x2: pt.x, y2: pt.y, selected: false };
    }

    this.isDrawing = true;
    this.isDragging = false;
    if (this.currentShape) {
      arrayToManage.push(this.currentShape);
    }
  }

  onShapeMouseDown(event: MouseEvent, shape: Shape, side: Side) {
    event.stopPropagation();
    event.preventDefault();
    this.capturePointer(event);

    const svg = (event.target as SVGElement).closest('svg');
    if (!svg) return;

    const pt = this.getSVGPoint(svg, event);
    if (!pt) return;

    this.selectShape(event, shape, side);
    this.isDragging = true;
    this.isDrawing = false;
    this.shapeMoved = false;
    this.currentShape = shape;
    this.dragStartPoint = { x: pt.x, y: pt.y };

    if (shape.type === 'circle') {
      this.dragOrigin = { cx: shape.cx, cy: shape.cy };
    } else {
      this.dragOrigin = { x1: shape.x1, y1: shape.y1, x2: shape.x2, y2: shape.y2 };
    }
  }

  onMouseMove(event: MouseEvent, _side: Side) {
    if ((!this.isDrawing && !this.isDragging) || !this.currentShape) return;
    event.preventDefault();

    const svg = this.getOverlaySvg(event);
    if (!svg) return;

    const pt = this.getSVGPoint(svg, event);
    if (!pt) return;

    if (this.isDragging && this.dragStartPoint && this.dragOrigin) {
      const dx = pt.x - this.dragStartPoint.x;
      const dy = pt.y - this.dragStartPoint.y;

      if (Math.hypot(dx, dy) > this.dragThreshold) {
        this.shapeMoved = true;
      }

      if (this.currentShape.type === 'circle') {
        this.currentShape.cx = (this.dragOrigin.cx ?? 0) + dx;
        this.currentShape.cy = (this.dragOrigin.cy ?? 0) + dy;
      } else {
        this.currentShape.x1 = (this.dragOrigin.x1 ?? 0) + dx;
        this.currentShape.y1 = (this.dragOrigin.y1 ?? 0) + dy;
        this.currentShape.x2 = (this.dragOrigin.x2 ?? 0) + dx;
        this.currentShape.y2 = (this.dragOrigin.y2 ?? 0) + dy;
      }
      return;
    }

    if (this.currentShape.type === 'circle') {
      const dx = pt.x - this.currentShape.cx;
      const dy = pt.y - this.currentShape.cy;
      this.currentShape.r = Math.sqrt(dx * dx + dy * dy);
    } else {
      this.currentShape.x2 = pt.x;
      this.currentShape.y2 = pt.y;
    }
  }

  onMouseUp(event: MouseEvent, side: Side) {
    this.releasePointer(event);
    if (this.isDragging && this.shapeMoved) {
      this.clearSelection(side);
    }

    this.isDrawing = false;
    this.isDragging = false;
    this.currentShape = null;
    this.dragStartPoint = null;
    this.dragOrigin = null;
  }

  selectShape(event: MouseEvent, shape: Shape, side: Side) {
    event.stopPropagation();
    if (this.shapeMoved) {
      this.shapeMoved = false;
      return;
    }

    this.shapesFor(side).forEach(s => s.selected = false);
    shape.selected = true;
    this.selectedShape = shape;
  }

  clearSelection(side: Side) {
    this.shapesFor(side).forEach(s => s.selected = false);
    this.selectedShape = null;
  }

  deleteSelectedShape() {
    if (!this.selectedShape) return;
    this.leftShapes = this.leftShapes.filter(s => s !== this.selectedShape);
    this.rightShapes = this.rightShapes.filter(s => s !== this.selectedShape);
    this.selectedShape = null;
  }

  clearAllShapes(event: Event, side: Side) {
    event.preventDefault();
    event.stopPropagation();
    const shapes = this.shapesFor(side);
    if (this.selectedShape && shapes.includes(this.selectedShape)) {
      this.selectedShape = null;
    }
    if (side === 'left') {
      this.leftShapes = [];
    } else {
      this.rightShapes = [];
    }
  }

  private shapesFor(side: Side): Shape[] {
    return side === 'left' ? this.leftShapes : this.rightShapes;
  }

  private setDrawingScrollLock(locked: boolean) {
    document.documentElement.classList.toggle('session-drawing-lock', locked);
    document.body.classList.toggle('session-drawing-lock', locked);
    document.removeEventListener('touchmove', this.preventTouchScroll);
    if (locked) {
      document.addEventListener('touchmove', this.preventTouchScroll, { passive: false });
    }
  }

  private preventTouchScroll = (event: TouchEvent) => {
    event.preventDefault();
  };

  private getSVGPoint(svg: SVGSVGElement | null, event: MouseEvent) {
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    return pt.matrixTransform(svg.getScreenCTM()?.inverse());
  }

  private getOverlaySvg(event: Event): SVGSVGElement | null {
    const from = (event.currentTarget as Element | null) ?? (event.target as Element | null);
    if (!from) {
      return null;
    }
    if (from instanceof SVGElement) {
      return from.closest('svg');
    }
    return from.closest('.video-content')?.querySelector('svg.overlay') as SVGSVGElement | null;
  }

  private capturePointer(event: MouseEvent) {
    const pointerEvent = event as PointerEvent;
    if (typeof pointerEvent.pointerId !== 'number') {
      return;
    }
    const target = (event.currentTarget as Element | null) ?? (event.target as Element | null)?.closest?.('svg');
    if (target && 'setPointerCapture' in target) {
      try {
        (target as Element).setPointerCapture(pointerEvent.pointerId);
      } catch {
        // Capture is best-effort; drawing still works without it.
      }
    }
  }

  private releasePointer(event: MouseEvent) {
    const pointerEvent = event as PointerEvent;
    if (typeof pointerEvent.pointerId !== 'number') {
      return;
    }
    const target = (event.currentTarget as Element | null) ?? (event.target as Element | null)?.closest?.('svg');
    if (target && 'releasePointerCapture' in target) {
      try {
        (target as Element).releasePointerCapture(pointerEvent.pointerId);
      } catch {
        // Already released.
      }
    }
  }
}
